import { ChevronDown, ChevronRight, Folder, FolderOpen, Folders } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { backendClient } from '@/lib/api-client'
import { Loading } from '../shared'
import Spinner from '../shared/Spinner'

export interface FolderItem {
  id: string
  name: string
  path: string
  has_children: boolean
  document_count?: number
  is_visible_default_folder?: boolean
  is_disabled?: boolean
  /** OneDrive: true for file rows (visible but not selectable) */
  is_file?: boolean
  /** OneDrive Graph drive id for the item */
  drive_id?: string
  /** SharePoint: true when this row represents a document library root. */
  is_drive_root?: boolean
  /** SharePoint document library display name for root selections. */
  drive_name?: string
  children?: FolderItem[]
  isExpanded?: boolean
}

interface WorkspacePickerProps {
  providerType: string
  providerConfig: Record<string, any>
  accessToken: string
  matterGuid: string
  onSelect: (folder: FolderItem | null, allSelectedFolders?: FolderItem[]) => void
  selectedFolderId?: string
  selectedFolderIds?: string[]
  multiSelect?: boolean
  className?: string
}

export function WorkspacePicker({
  matterGuid,
  onSelect,
  selectedFolderIds = [],
  className
}: WorkspacePickerProps) {
  const [folders, setFolders] = useState<FolderItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>(selectedFolderIds)
  const [selectedFoldersMap, setSelectedFoldersMap] = useState<Map<string, FolderItem>>(new Map())
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(new Set())
  const [loadingFolderIds, setLoadingFolderIds] = useState<Set<string>>(new Set())

  const loadRootFolders = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await backendClient.get(`/correspondence/${matterGuid}/outlook-folders`, {
        params: { parent_id: null }
      })
      setFolders(response.data.folders || [])
    } catch (err: any) {
      const detail = err.response?.data?.detail
      const errorMessage = detail?.code === 'graph_consent_required'
        ? detail.message
        : (typeof detail === 'string' ? detail : err.message || 'Failed to load folders')
      setError(errorMessage)
      setFolders([])
    } finally {
      setIsLoading(false)
    }
  }, [matterGuid])

  useEffect(() => {
    loadRootFolders()
  }, [loadRootFolders])

  const loadChildrenForFolder = async (folder: FolderItem): Promise<FolderItem[]> => {
    try {
      const response = await backendClient.get(`/correspondence/${matterGuid}/outlook-folders`, {
        params: { parent_id: folder.id }
      })
      return response.data.folders || []
    } catch (err: any) {
      console.error('Failed to load children:', err)
      return []
    }
  }

  const updateFolderInTree = (folders: FolderItem[], targetId: string, updater: (folder: FolderItem) => FolderItem): FolderItem[] => {
    return folders.map(folder => {
      if (folder.id === targetId) {
        return updater(folder)
      }
      if (folder.children && folder.children.length > 0) {
        return {
          ...folder,
          children: updateFolderInTree(folder.children, targetId, updater)
        }
      }
      return folder
    })
  }

  const toggleFolderExpansion = async (folder: FolderItem) => {
    const isExpanded = expandedFolderIds.has(folder.id)

    if (isExpanded) {
      setExpandedFolderIds(prev => {
        const next = new Set(prev)
        next.delete(folder.id)
        return next
      })
    } else {
      if (!folder.children && folder.has_children) {
        setLoadingFolderIds(prev => new Set(prev).add(folder.id))

        try {
          const children = await loadChildrenForFolder(folder)
          setFolders(prevFolders =>
            updateFolderInTree(prevFolders, folder.id, (f) => ({ ...f, children }))
          )
        } finally {
          setLoadingFolderIds(prev => {
            const next = new Set(prev)
            next.delete(folder.id)
            return next
          })
        }
      }
      setExpandedFolderIds(prev => new Set(prev).add(folder.id))
    }
  }

  const handleFolderClick = (folder: FolderItem) => {
    if (folder.is_disabled) {
      return
    }

    const isSelected = localSelectedIds.includes(folder.id)
    const newSelection = isSelected
      ? localSelectedIds.filter(id => id !== folder.id)
      : [...localSelectedIds, folder.id]

    setLocalSelectedIds(newSelection)

    const newMap = new Map(selectedFoldersMap)
    if (isSelected) {
      newMap.delete(folder.id)
    } else {
      newMap.set(folder.id, folder)
    }

    setSelectedFoldersMap(newMap)

    const allSelectedFolders = Array.from(newMap.values())
    onSelect(allSelectedFolders.length > 0 ? allSelectedFolders[0] : null, allSelectedFolders)
  }

  const renderFolderTree = (folder: FolderItem, depth: number = 0): ReactNode => {
    const isSelected = localSelectedIds.includes(folder.id)
    const isDisabled = folder.is_disabled || false
    const isExpanded = expandedFolderIds.has(folder.id)
    const isFolderLoading = loadingFolderIds.has(folder.id)

    return (
      <div key={folder.id}>
        <div
          className={`flex items-center ${isDisabled ? 'bg-gray-50' : ''} ${
            isSelected ? 'bg-blue-50' : ''
          }`}
          style={{ paddingLeft: `${depth * 20 + 12}px` }}
        >
          {folder.has_children ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleFolderExpansion(folder)
                    }}
                    className="mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 shadow-xs transition-colors hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700"
                    disabled={isFolderLoading}
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${folder.name}`}
                    aria-expanded={isExpanded}
                  >
                    {isFolderLoading ? (
                      <Spinner size='sm' />
                    ) : isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {isExpanded ? 'Collapse subfolders' : 'Expand subfolders'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <div className="w-6 mr-1" />
          )}

          <div
            onClick={() => !isDisabled && handleFolderClick(folder)}
            className={`flex-1 py-2 pr-3 flex items-center gap-2 transition-colors ${
              isDisabled ? 'cursor-not-allowed' : 'hover:bg-gray-100 cursor-pointer'
            }`}
          >
            {!isDisabled && (
              <Checkbox
                checked={isSelected}
                className="shrink-0"
                aria-label={`Select ${folder.name}`}
                onClick={(e) => e.stopPropagation()}
                onCheckedChange={(_checked) => {
                  handleFolderClick(folder)
                }}
              />
            )}
            {isExpanded ? (
              <FolderOpen className={`h-4 w-4 shrink-0 ${isDisabled ? 'text-gray-400' : 'text-amber-500/40 fill-amber-400/15'}`} />
            ) : folder.has_children ? (
              <Folders className={`h-4 w-4 shrink-0 ${isDisabled ? 'text-gray-400' : 'text-amber-500/40 fill-amber-400/15'}`} />
            ) : (
              <Folder className={`h-4 w-4 shrink-0 ${isDisabled ? 'text-gray-400' : 'text-amber-500/40 fill-amber-400/15'}`} />
            )}
            <div className="flex-1 min-w-0">
              <div className={`truncate text-sm font-medium ${isDisabled ? 'text-gray-500' : 'text-gray-900'}`}>
                {folder.name}
              </div>
            </div>
          </div>
        </div>

        {isExpanded && folder.children && (
          <div>
            {folder.children.map(child => renderFolderTree(child, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={`flex flex-col h-full ${className || ''}`}>
      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loading text='Loading folders...' />
          </div>
        ) : error ? (
          <div className="p-4 text-center text-red-600 text-sm">
            {error}
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadRootFolders()}
              className="mt-2"
              text="Retry"
            />
          </div>
        ) : folders.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            No folders found
          </div>
        ) : (
          <div className="py-2">
            {folders.map(folder => renderFolderTree(folder, 0))}
          </div>
        )}
      </ScrollArea>

      <div className="px-4 py-3 border-t bg-gray-50 text-xs text-gray-600">
        {localSelectedIds.length > 0
          ? `${localSelectedIds.length} folder${localSelectedIds.length === 1 ? '' : 's'} selected (explicitly select subfolders to include them in the sync)`
          : 'Select one or more folders to sync'}
      </div>
    </div>
  )
}
